import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createVoucher,
  fetchAllVouchers,
  updateVoucher,
  fetchOneVoucher,
  deleteVoucher,
  viewVoucherReport,
  getNextVoucherNumber,
  getOutstandingBills,
} from "services/voucher";

export const useAllVouchers = (filters = {}, enabled = true) => {
  const query = useQuery({
    queryKey: ["all-vouchers", filters],
    queryFn: () => fetchAllVouchers(filters),
    enabled: enabled,
  });
  return query;
};

export const useOneVoucher = (id, enabled = true) => {
  const query = useQuery({
    queryKey: ["one-voucher", id],
    queryFn: () => {
      if (id) {
        return fetchOneVoucher(id);
      }
      return null;
    },
    enabled: Boolean(id) && enabled,
  });
  return query;
};

export const useCreateVoucher = () => {
  const queryClient = useQueryClient();
  
  const query = useMutation({
    mutationFn: (data) => createVoucher(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-vouchers"] });
    },
  });
  return query;
};

export const useUpdateVoucher = () => {
  const queryClient = useQueryClient();
  
  const query = useMutation({
    mutationFn: ({ id, data }) => updateVoucher(id, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["all-vouchers"] });
      queryClient.invalidateQueries({ queryKey: ["one-voucher", variables.id] });
    },
  });
  return query;
};

export const useDeleteVoucher = () => {
  const queryClient = useQueryClient();
  
  const query = useMutation({
    mutationFn: (id) => deleteVoucher(id),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: ["all-vouchers"] });
      queryClient.invalidateQueries({ queryKey: ["one-voucher", id] });
    },
  });
  return query;
};

export const useViewVoucherReport = () => {
  const query = useMutation({
    mutationFn: (id) => viewVoucherReport(id),
  });
  return query;
};

export const useNextVoucherNumber = (trtyp, enabled = true) => {
  const query = useQuery({
    queryKey: ["next-voucher-number", trtyp],
    queryFn: () => getNextVoucherNumber(trtyp),
    enabled: enabled,
    staleTime: 0, // Always fetch fresh data
  });
  return query;
};

export const useOutstandingBills = (vendorId, customerId, enabled = true) => {
  const query = useQuery({
    queryKey: ["outstanding-bills", vendorId, customerId],
    queryFn: () => getOutstandingBills(vendorId, customerId),
    enabled: Boolean(vendorId && customerId) && enabled,
    staleTime: 0, // Always fetch fresh data
  });
  return query;
};
